import React, { useState } from 'react';
import { Button } from '../UI/Button';
import { Card } from '../UI/Card';
import { CheckCircle2 } from 'lucide-react';
export const TrainingProposalForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    role: '',
    teamSize: '',
    trainingInterest: '',
    preferredFormat: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      // Using the existing formspree endpoint pattern from the codebase
      const response = await fetch('https://formspree.io/f/mjgavwrk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          _subject: `Training Proposal Request from ${formData.organization}`
        })
      });
      if (response.ok) {
        setIsSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          organization: '',
          role: '',
          teamSize: '',
          trainingInterest: '',
          preferredFormat: ''
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (err) {
      setError('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <section id="training-proposal-form" className="py-20 bg-white" data-id="element-1366" data-mp="2c7y7idrovy7p">
        <div className="container mx-auto px-4" data-id="element-1367" data-mp="278v4eyv9s22d">
          <Card className="max-w-2xl mx-auto p-12 text-center border-2 border-green-100 bg-green-50/30" data-id="element-1368" data-mp="2770ibh89i3wp">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1369" data-mp="28qk07r8xugxh">
              <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-1370" data-mp="2dmr6xm95rwgh" />
            </div>
            <h3 className="text-3xl font-bold text-navy mb-4 font-heading" data-id="element-1371" data-mp="1zoj7n7ongj25">
              Request Received!
            </h3>
            <p className="text-lg text-gray-700 mb-8" data-id="element-1372" data-mp="23sijve2wcvpp">
              Thank you for your interest in training with The Networking Salon.
              Our team is preparing a custom training proposal based on your
              selections and will send it to your email shortly.
            </p>
            <Button onClick={() => setIsSuccess(false)} data-id="element-1373" data-mp="1fz8otktlxhzt">
              Submit Another Request
            </Button>
          </Card>
        </div>
      </section>;
  }
  return <section id="training-proposal-form" className="py-20 bg-white" data-id="element-1374" data-mp="170wyzl0keo8f">
      <div className="container mx-auto px-4" data-id="element-1375" data-mp="1bygespcwv92h">
        <div className="max-w-3xl mx-auto scroll-animate" data-id="element-1376" data-mp="1c1ktigo05n7e">
          <div className="text-center mb-12" data-id="element-1377" data-mp="sehj8p0rllza">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4 font-heading" data-id="element-1378" data-mp="sfj4fmbhvouq">
              Train Your Team With Us
            </h2>
            <p className="text-xl text-gray-600" data-id="element-1379" data-mp="11ffotbquq1jz">
              Fill out the form below to receive a custom training proposal PDF
              tailored to your organization's needs.
            </p>
          </div>

          <Card className="p-8 md:p-10 border-2 border-gray-200 shadow-lg" data-id="element-1380" data-mp="1bb97zdz2staq">
            {error && <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md mb-6" data-id="element-1381" data-mp="16cy0h9w96uvo">
                {error}
              </div>}

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1382" data-mp="1g3itmht5r777">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1383" data-mp="1g5di263n1hg1">
                <div data-id="element-1384" data-mp="1u3tpy0d8ldzr">
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-2" data-id="element-1385" data-mp="1u0z3y4ekrl48">
                    Full Name *
                  </label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors" placeholder="Jane Doe" data-id="element-1386" data-mp="3ji2z1kzpku3z" />
                </div>
                <div data-id="element-1387" data-mp="35l7f2xhx5i95">
                  <label htmlFor="organization" className="block text-gray-700 font-medium mb-2" data-id="element-1388" data-mp="35ko6w8i2m88b">
                    Organization *
                  </label>
                  <input type="text" id="organization" name="organization" value={formData.organization} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors" placeholder="Acme Corp" data-id="element-1389" data-mp="3eiq56gaf6mrw" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1390" data-mp="q7dp0k36bekf">
                <div data-id="element-1391" data-mp="q75bcpz88586">
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-2" data-id="element-1392" data-mp="q5kprpuw3wmg">
                    Email Address *
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors" placeholder="jane@example.com" data-id="element-1393" data-mp="b9v27na23bfm" />
                </div>
                <div data-id="element-1394" data-mp="3v36qabnyar8i">
                  <label htmlFor="phone" className="block text-gray-700 font-medium mb-2" data-id="element-1395" data-mp="3qlvk1sxpqu9">
                    Phone Number *
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors" placeholder="+234 800 000 0000" data-id="element-1396" data-mp="3q5opj8k37ibl" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1397" data-mp="2tg7ba571gnya">
                <div data-id="element-1398" data-mp="2ti0lfugv1qb1">
                  <label htmlFor="role" className="block text-gray-700 font-medium mb-2" data-id="element-1399" data-mp="2yf0mfqyus2va">
                    Your Role *
                  </label>
                  <input type="text" id="role" name="role" value={formData.role} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors" placeholder="HR Director" data-id="element-1400" data-mp="2lwojb9fthkdk" />
                </div>
                <div data-id="element-1401" data-mp="226cj70d610l2">
                  <label htmlFor="teamSize" className="block text-gray-700 font-medium mb-2" data-id="element-1402" data-mp="2gxboo2ipf90f">
                    Team Size *
                  </label>
                  <select id="teamSize" name="teamSize" value={formData.teamSize} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors bg-white" data-id="element-1403" data-mp="1ogr4i26ef7iu">
                    <option value="" data-id="element-1404" data-mp="17wslbj0brd9b">Select team size</option>
                    <option value="1-10" data-id="element-1405" data-mp="17vhgrgz221h5">1 - 10 people</option>
                    <option value="11-25" data-id="element-1406" data-mp="1cw5gqs59hsni">11 - 25 people</option>
                    <option value="26-50" data-id="element-1407" data-mp="1gxgyyclt3blo">26 - 50 people</option>
                    <option value="50+" data-id="element-1408" data-mp="1gwxpzle4vxjs">50+ people</option>
                  </select>
                </div>
              </div>

              <div data-id="element-1409" data-mp="1lu7dgvhypz4z">
                <label htmlFor="trainingInterest" className="block text-gray-700 font-medium mb-2" data-id="element-1410" data-mp="1qnk2v7ugu9ys">
                  Primary Training Interest *
                </label>
                <select id="trainingInterest" name="trainingInterest" value={formData.trainingInterest} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors bg-white" data-id="element-1411" data-mp="20m9bsb4dwk2w">
                  <option value="" data-id="element-1412" data-mp="3g3md5s8s7i3m">Select an area of interest</option>
                  <option value="AI Training" data-id="element-1413" data-mp="3b5b3pimef9lx">
                    AI Training & Productivity
                  </option>
                  <option value="Learning & Development" data-id="element-1414" data-mp="co76n0zm3qn8">
                    Learning & Development
                  </option>
                  <option value="Teacher Retooling" data-id="element-1415" data-mp="7pxb2p9z6o13">
                    Teacher Retooling Programs
                  </option>
                  <option value="Communications" data-id="element-1416" data-mp="2vsz1rlurpt5">
                    Communications & Soft Skills
                  </option>
                  <option value="Strategic Networking" data-id="element-1417" data-mp="vdwybf82vwax">
                    Strategic Networking
                  </option>
                  <option value="Digital Growth" data-id="element-1418" data-mp="qn6vo9r1e7jx">
                    Social Media & Digital Growth
                  </option>
                  <option value="Community Building" data-id="element-1419" data-mp="1abq0z6tancqj">
                    Community Building & Management
                  </option>
                  <option value="Program Development" data-id="element-1420" data-mp="1k9dyu5m750cx">
                    Program Development
                  </option>
                  <option value="Other" data-id="element-1421" data-mp="1f9rmrzi3tq5p">Other (Custom Program)</option>
                </select>
              </div>

              <div data-id="element-1422" data-mp="1t9akdydahj3r">
                <label htmlFor="preferredFormat" className="block text-gray-700 font-medium mb-2" data-id="element-1423" data-mp="1t8hibap9zhry">
                  Preferred Training Format *
                </label>
                <select id="preferredFormat" name="preferredFormat" value={formData.preferredFormat} onChange={handleChange} required className="w-full px-4 py-3 border-2 border-gray-300 rounded-md focus:outline-none focus:border-primary transition-colors bg-white" data-id="element-1424" data-mp="231yaybdoequ1">
                  <option value="" data-id="element-1425" data-mp="2qo22hh3qtmn1">Select format</option>
                  <option value="On-site" data-id="element-1426" data-mp="2vmlnnfaiiuec">On-site (In person)</option>
                  <option value="Virtual" data-id="element-1427" data-mp="2hmklzxmfbixt">Virtual (Online)</option>
                  <option value="Hybrid" data-id="element-1428" data-mp="2hmaw0a1dpbs4">Hybrid (Mix of both)</option>
                  <option value="Undecided" data-id="element-1429" data-mp="2mkm70o3etsbx">Undecided</option>
                </select>
              </div>

              <div className="pt-4" data-id="element-1430" data-mp="27vzetlsop63a">
                <Button type="submit" disabled={isSubmitting} className="w-full py-4 text-lg shadow-md" data-id="element-1431" data-mp="27uy3dg7rahl1">
                  {isSubmitting ? 'Generating Proposal...' : 'Request Training Proposal'}
                </Button>
                <p className="text-center text-sm text-gray-500 mt-4" data-id="element-1432" data-mp="1oxde4812xgzw">
                  After submission, we'll generate and send a custom training
                  proposal PDF to your organization.
                </p>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </section>;
};